import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CUSTOMER_TYPES, SOURCES, DESIRED_LISTING } from "@/lib/crmOptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const EMPTY = {
  full_name: "", phone: "", email: "", customer_type: "BUYER", source: "PHONE", desired_listing_type: "ANY",
  desired_city: "", desired_district: "", desired_neighborhood: "", budget_min: "", budget_max: "", preferences: "", notes: "",
};

const Pick = ({ label, value, onChange, options, testId }) => (
  <div>
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{Object.entries(options).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const CustomerForm = ({ open, editing, onClose, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (open) setForm(editing ? Object.fromEntries(Object.keys(EMPTY).map((k) => [k, editing[k] ?? EMPTY[k]])) : EMPTY);
  }, [open, editing]);

  const submit = async (e) => {
    e.preventDefault();
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const payload = { ...form, budget_min: num(form.budget_min), budget_max: num(form.budget_max), email: form.email || null };
    try {
      if (editing) await api.put(`/customers/${editing.id}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Müşteri güncellendi" : "Müşteri kaydedildi");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Müşteriyi Düzenle" : "Yeni Müşteri Kaydı"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Ad Soyad *</Label><Input data-testid="customer-name-input" required value={form.full_name} onChange={set("full_name")} /></div>
            <div><Label>Telefon *</Label><Input data-testid="customer-phone-input" required value={form.phone} onChange={set("phone")} /></div>
            <div><Label>E-posta</Label><Input type="email" value={form.email} onChange={set("email")} /></div>
            <Pick label="Kategori" testId="customer-type-select" value={form.customer_type} onChange={set("customer_type")} options={CUSTOMER_TYPES} />
            <Pick label="Geliş Şekli" testId="customer-source-select" value={form.source} onChange={set("source")} options={SOURCES} />
          </div>

          <div className="rounded-lg border border-slate-200 p-4 space-y-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Talep (otomatik eşleştirme için)</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Pick label="İlan Tipi" testId="customer-desired-listing" value={form.desired_listing_type || "ANY"} onChange={set("desired_listing_type")} options={DESIRED_LISTING} />
              <div><Label>Min Bütçe (TL)</Label><Input data-testid="customer-budget-min" type="number" min="0" value={form.budget_min} onChange={set("budget_min")} /></div>
              <div><Label>Max Bütçe (TL)</Label><Input data-testid="customer-budget-max" type="number" min="0" value={form.budget_max} onChange={set("budget_max")} /></div>
              <div><Label>İl</Label><Input data-testid="customer-desired-city" value={form.desired_city} onChange={set("desired_city")} /></div>
              <div><Label>İlçe</Label><Input data-testid="customer-desired-district" value={form.desired_district} onChange={set("desired_district")} /></div>
              <div><Label>Mahalle</Label><Input value={form.desired_neighborhood} onChange={set("desired_neighborhood")} /></div>
            </div>
            <div><Label>Aradığı özellikler (anahtar kelimeler)</Label><Textarea data-testid="customer-preferences" rows={2} value={form.preferences} onChange={set("preferences")} placeholder="Örn: 3+1, deniz manzaralı, güney cephe, site içi" /></div>
          </div>

          <div><Label>Genel Not</Label><Textarea rows={2} value={form.notes} onChange={set("notes")} /></div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={onClose}>İptal</Button>
            <Button data-testid="customer-save-btn" type="submit" className="bg-emerald-600 hover:bg-emerald-700">Kaydet</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
